export type BankAccount = {
  accountId: number;
  iban: string;
  accountName: string;
  bank: Bank;
  dashboardId: number;
  balance: number; // in cents
  requisitionId?: string;
  requisitionExpiryDate?: Date;
  transferCompleted: boolean;
  latestFetching?: Date;
  goCardlessAccountId?: string;
  autoFetchEnabled?: boolean;
  autoFetchDisabledReason?: AutoFetchDisabledReason;
  gocardlessAutomaticFetchCount?: number;
  gocardlessManualFetchCount?: number;
};

export type AutoFetchDisabledReason = 'GC_ACCESS_EXPIRED' | 'USER_DISABLED' | string;

export enum Bank {
  ABN_AMRO = 'ABN_AMRO',
  ASN_BANK = 'ASN_BANK',
  BUNQ = 'BUNQ',
  ING = 'ING',
  RABOBANK = 'RABOBANK',
  SNS_BANK = 'SNS_BANK',
}

/**
 * @deprecated Use JournalEntrySourceType instead. This enum is kept for backward compatibility during migration.
 */
export enum TransactionType {
  // positive sum that is assigned to a (group of) member(s); does not change group balance; changes personal balance
  PERSONAL_INCOME = 'PERSONAL_INCOME',

  // negative sum that is assigned to a (group of) member(s); does not change group balance; changes personal balance
  PERSONAL_EXPENSE = 'PERSONAL_EXPENSE',

  // negative sum that has no assignees; changes group balance; does not change personal balance
  GROUP_EXPENSE = 'GROUP_EXPENSE',

  // positive sum that has no assignees; changes group balance; does not change personal balance
  GROUP_INCOME = 'GROUP_INCOME',

  // a type of group income -- in the future this can become its own model
  // positive sum that has one assignee; changes group balance; does not change personal balance
  CONTRIBUTION = 'CONTRIBUTION',

  // internal transfer between two bank accounts of the same dashboard; excluded from P&L
  INTERNAL_BANK_TRANSFER = 'INTERNAL_BANK_TRANSFER',
}

// Journal Entry Source Types
export type JournalEntrySourceType =
  | 'CONTRIBUTION_RUN'
  | 'SAVING_POT_FUND'
  | 'SAVING_POT_RELEASE'
  | 'BANK_RECONCILIATION'
  | 'PERSONAL_EXPENSE'
  | 'PERSONAL_INCOME'
  | 'GROUP_EXPENSE'
  | 'GROUP_INCOME'
  | 'INTERNAL_TRANSFER'
  | 'INITIAL_BALANCE';

export const JOURNAL_ENTRY_SOURCE_TYPES = {
  CONTRIBUTION_RUN: 'CONTRIBUTION_RUN',
  SAVING_POT_FUND: 'SAVING_POT_FUND',
  SAVING_POT_RELEASE: 'SAVING_POT_RELEASE',
  BANK_RECONCILIATION: 'BANK_RECONCILIATION',
  PERSONAL_EXPENSE: 'PERSONAL_EXPENSE',
  PERSONAL_INCOME: 'PERSONAL_INCOME',
  GROUP_EXPENSE: 'GROUP_EXPENSE',
  GROUP_INCOME: 'GROUP_INCOME',
  INTERNAL_TRANSFER: 'INTERNAL_TRANSFER',
  INITIAL_BALANCE: 'INITIAL_BALANCE',
} as const;

export type CreateBankAccountModel = Omit<
  BankAccount,
  | 'requisitionId'
  | 'requisitionExpiryDate'
  | 'transferCompleted'
  | 'accountId'
  | 'dashboardId'
  | 'balance'
>;

export type BankMutation = {
  mutationId: number;
  accountId: number;
  sequenceNumber: number;
  amount: number; // in cents
  balanceAfter: number; // in cents
  description: string;
  oppositeName: string;
  oppositeAccount: string;
  transactionDate: Date;

  // Details from GoCardless
  bankTransactionId?: string;
  goCardLessTransactionId?: string;
  externalData?: object;

  // Details for Prisma
  journalEntryCount: number;
};

export type CreateBankMutationModel = Omit<
  BankMutation,
  'mutationId' | 'accountId' | 'journalEntryCount' | 'sequenceNumber'
>;

export type BankTransaction = {
  transactionId: number;
  dashboardId: number;
  mutationId?: number;
  payerId?: number;
  processedByDashboardMemberId?: number;
  contributionPaymentId?: number;
  transactionType: TransactionType;
  amount: number;
  date: Date;
  description: string;
  contributionPayment?: {
    contributionPlanId: number;
    dashboardMemberId: number;
  };
  processedByDashboardMember?: {
    dashboardMemberId: number;
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      picture: string | null;
    };
  } | null;
};

export type BankTransactionWithParticipationModel = BankTransaction & {
  participations?: BankTransactionParticipation[];
};

// Deprecated: Use journal entries instead
export type BankAccountWithTransactions = BankAccount & {
  transactions: BankTransaction[];
};

export type CreateBankTransactionModel = Omit<BankTransaction, 'transactionId' | 'dashboardId'> & {
  participations?: BankTransactionParticipation[];
  contributionPayment?: {
    contributionPlanId: number;
    dashboardMemberId: number;
  };
};

export type BankTransactionWithParticipation = BankTransaction & {
  participations: BankTransactionParticipation[] | null;
};

export type InternalBankTransfer = {
  id: number;
  dashboardId: number;
  // Note: Internal transfers now use INTERNAL_TRANSFER source type in journal entries
  // Transaction IDs have been removed in favor of journal entry-based tracking
};

export type BankTransactionParticipation = {
  dashboardMemberId: number;
  amount: number;
};

export interface GoCardlessInstitution {
  id: string;
  name: string;
  logo: string;
  countries: string[];

  bic?: string;
  transaction_total_days?: string;
  supported_features?: string[];
}

export interface CreateGoCardlessLinkBody {
  redirect: string;
  institution_id: string;
  user_language: 'en' | 'nl';
  agreement?: string;
}

export interface GoCardlessLink {
  redirect: string;
  link: string;
  institution_id: string;

  id: string;
  created: string; // ISO Date
  status: 'CR' | 'ID' | 'LN' | 'RJ' | 'ER' | 'SU' | 'EX' | 'GC' | 'UA' | 'GA' | 'SA';
  agreement: string;
  reference: string;
  accounts: string[];
  user_language: 'en' | 'nl';
  ssn: string;
  account_selection: boolean;
  redirect_immediate: boolean;
}

export interface GoCardlessAccount {
  id: string;
  created: string; // ISO Date
  last_accessed: string; // ISO Date
  iban: string;
  status: string;
  institution_id: string;
  owner_name: string;
  name: string;
}

export enum ContributionFrequency {
  ONCE = 'ONCE',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export interface ContributionPlanModel {
  contributionPlanId: number;
  name: string;
  description: string;
  amount: number; // in cents, full amount
  frequency: ContributionFrequency;
  startDate: Date;
  // endDate?: Date;
  numberOfInstallments: number;
  dashboardId: number;

  // contributionPayments ContributionPayment[]
  // appliesTo            ContributionPlanOnMembers[]
}

export type ContributionPlanWithMembersModel = ContributionPlanModel & {
  appliesTo: { dashboardMemberId: number }[];
};

export type CreateContributionPlanDto = Omit<
  ContributionPlanModel,
  'contributionPlanId' | 'dashboardId'
> & {
  appliesToDashboardMemberId: number[];
};

export interface ContributionPlanOnMembersModel {
  contributionPlanId: number;
  dashboardMemberId: number;

}

export interface ContributionPaymentModel {
  contributionPaymentId: number;
  contributionPlanId: number;
  dashboardMemberId: number;
  journalEntryId: number | null;
}

export type CreateContributionPaymentDto = Omit<ContributionPaymentModel, 'contributionPaymentId'>;

// Journal Entry Types
export interface JournalEntryLineCategoryRef {
  financialCategoryId: number;
  category: {
    financialCategoryId: number;
    name: string;
    color: string;
    isSystem: boolean;
  };
}

export interface JournalEntryLineModel {
  journalEntryLineId: number;
  journalEntryId: number;
  ledgerAccountId: number;
  participantMemberId: number | null;
  debit: number; // in cents (always positive)
  credit: number; // in cents (always positive)
  currency: Currency;
  categories?: JournalEntryLineCategoryRef[];
}

export interface JournalEntryModel {
  journalEntryId: number;
  dashboardId: number;
  dashboardMemberId?: number; // Nullable for group transactions
  payerId?: number;
  processedByDashboardMemberId?: number;
  processedByDashboardMember?: {
    dashboardMemberId: number;
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      picture: string | null;
    };
  } | null;
  entryDate: Date;
  description: string;
  sourceType: JournalEntrySourceType;
  sourceId?: number;
  mutationId?: number;
  lines?: JournalEntryLineModel[]; // Debit/credit lines
}

// Create Journal Entry DTOs
export interface CreateJournalEntryLineDto {
  ledgerAccountId: number;
  participantMemberId?: number;
  debit: number; // in cents (always positive)
  credit: number; // in cents (always positive)
  currency: Currency;
}

export interface CreateJournalEntryDto {
  dashboardId: number;
  dashboardMemberId?: number;
  payerId?: number;
  processedByDashboardMemberId?: number;
  entryDate: Date;
  description: string;
  sourceType: JournalEntrySourceType;
  sourceId?: number;
  mutationId?: number;
  amount?: number; // Amount in cents (required for GROUP_EXPENSE)
  currency?: Currency; // Currency code (optional, defaults to "EUR")
  lines: CreateJournalEntryLineDto[];
  participations?: BankTransactionParticipation[];
}

// Legacy alias for backward compatibility during migration
export interface JournalEntryLine extends JournalEntryLineModel {}
export interface JournalEntry extends JournalEntryModel {}

export type ContributionPaymentWithTransactionDetails = ContributionPaymentModel & {
  journalEntry: JournalEntryModel | null;
};

export type ContributionPlanWithPayments = ContributionPlanWithMembersModel & {
  contributionPayments: ContributionPaymentWithTransactionDetails[];
};

export type BalanceStats = {
  income: number;
  expenses: number;
  balance: number;
};

export type BalanceSheet = {
  assets: {
    bankAccounts: {
      accounts: {
        accountName: string;
        balance: number;
      }[];
      totalBalance: number;
    };
    receivables: {
      negativePersonalBalance: number;
      contributionReceivables: number;
      totalReceivables: number;
    };
    unassigned: {
      unassignedExpenses: number;
      totalUnassigned: number;
    };
    totalAssets: number;
  };
  liabilitiesAndEquity: {
    equity: {
      initialEquity: number;
      profitSinceStart: number;
      totalEquity: number;
    };
    liabilities: {
      positivePersonalBalance: number;
      contributionOverPayments: number;
      totalLiabilities: number;
    };
    unassigned: {
      unassignedIncome: number;
      totalUnassigned: number;
    };
    totalLiabilitiesAndEquity: number;
  };
};

export interface ProfitAndLoss {
  groupIncome: number; // in cents
  groupExpenses: number; // in cents (likely negative amounts)
  contributionExpenses: number; // in cents
}

// Currency enum for ledger-based wallet system
export enum Currency {
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
  BGN = 'BGN',
  CZK = 'CZK',
  DKK = 'DKK',
  HUF = 'HUF',
  PLN = 'PLN',
  RON = 'RON',
  SEK = 'SEK',
  CHF = 'CHF',
  ISK = 'ISK',
  NOK = 'NOK',
  HRK = 'HRK',
  RUB = 'RUB',
  TRY = 'TRY',
  AUD = 'AUD',
  BRL = 'BRL',
  CAD = 'CAD',
  CNY = 'CNY',
  HKD = 'HKD',
  IDR = 'IDR',
  ILS = 'ILS',
  INR = 'INR',
  KRW = 'KRW',
  MXN = 'MXN',
  MYR = 'MYR',
  NZD = 'NZD',
  PHP = 'PHP',
  SGD = 'SGD',
  THB = 'THB',
  ZAR = 'ZAR',
}

// Ledger Account Types
export enum LedgerAccountType {
  ASSET = 'ASSET',
  LIABILITY = 'LIABILITY',
  REVENUE = 'REVENUE',
  EXPENSE = 'EXPENSE',
  EQUITY = 'EQUITY',
}

// Ledger Account
export interface LedgerAccount {
  ledgerAccountId: number;
  dashboardId: number;
  dashboardMemberId: number | null;
  accountCode: string;
  accountName: string;
  accountType: LedgerAccountType;
  currency: Currency;
  isActive: boolean;
}

// Wallet Balance Response
export interface WalletBalanceResponse {
  balance: number; // in cents
  currency: Currency;
}

// Saving Pot
export interface SavingPot {
  savingPotId: number;
  dashboardId: number;
  name: string;
  targetAmount: number | null; // in cents, optional
  currency: Currency;
  isActive: boolean;
  ledgerAccountId: number;
}

// Saving Pot with participant info returned from list endpoint
export type SavingPotWithParticipants = SavingPot & {
  dashboardMemberIds: number[];
};

// Create Saving Pot DTO
export interface CreateSavingPotDto {
  name: string;
  targetAmount?: number; // in cents, optional
  currency: Currency;
  isActive?: boolean;
}

// Update Saving Pot DTO
export interface UpdateSavingPotDto {
  name?: string;
  targetAmount?: number | null; // Set to null to remove target
  isActive?: boolean;
}

// Fund Saving Pot DTO
export interface FundSavingPotDto {
  amountInCents: number;
  description?: string;
}

// Release from Saving Pot DTO
export interface ReleaseSavingPotDto {
  amountInCents: number;
  description?: string;
}

// Saving Pot Balance Response
export interface SavingPotBalanceResponse {
  balance: number; // in cents
  savingPotId: number;
  memberId?: number; // Only present for member balance queries
}

// Run Contribution Plans Response
export interface RunContributionPlansResponse {
  posted: number;
  skipped: number;
}

// Financial Categories
export interface FinancialCategory {
  financialCategoryId: number;
  dashboardId: number;
  name: string;
  description?: string | null;
  color: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export type CreateFinancialCategoryDto = {
  name: string;
  description?: string | null;
  color: string;
  sortOrder?: number;
  isActive?: boolean;
};

export type UpdateFinancialCategoryDto = Partial<
  Pick<FinancialCategory, 'name' | 'description' | 'color' | 'isActive' | 'sortOrder'>
>;

// Categorization Rules
export type CategorizationRuleMatchType = 'CONTAINS' | 'EQUALS' | 'REGEX';

export interface CategorizationRule {
  categorizationRuleId: number;
  dashboardId: number;
  name: string;
  matchType: CategorizationRuleMatchType;
  matchText: string;
  financialCategoryId: number;
  priority: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type CreateCategorizationRuleDto = Omit<
  CategorizationRule,
  'categorizationRuleId' | 'dashboardId' | 'createdAt' | 'updatedAt'
>;

export type UpdateCategorizationRuleDto = Partial<
  Pick<
    CategorizationRule,
    'name' | 'matchType' | 'matchText' | 'financialCategoryId' | 'priority' | 'isActive'
  >
>;

export interface CategorizationRuleApplyResult {
  totalLinesScanned: number;
  assignmentsCreated: number;
  byRule: { ruleId: number; ruleName: string; matched: number }[];
}

// Book Years
export interface BookYear {
  bookYearId: number;
  dashboardId: number;
  startDate: Date;
  endDate: Date;
  label?: string | null;
  isClosed: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type CreateBookYearDto = {
  startDate: Date;
  endDate: Date;
  label?: string | null;
};

export type UpdateBookYearDto = Partial<
  Pick<BookYear, 'startDate' | 'endDate' | 'label' | 'isClosed'>
>;

// Budget
export interface BudgetLine {
  budgetLineId: number;
  budgetId: number;
  financialCategoryId: number;
  budgetedIncome: number;
  budgetedExpense: number;
  category?: {
    financialCategoryId: number;
    name: string;
    color: string;
    isSystem: boolean;
  };
}

export interface Budget {
  budgetId: number;
  dashboardId: number;
  bookYearId: number;
  totalIncome: number;
  totalExpense: number;
  notes?: string | null;
  lines: BudgetLine[];
  createdAt: Date;
  updatedAt: Date;
}

export interface UpsertBudgetLineDto {
  financialCategoryId: number;
  budgetedIncome: number;
  budgetedExpense: number;
}

export interface UpsertBudgetDto {
  notes?: string | null;
  lines: UpsertBudgetLineDto[];
}

export interface BudgetReportRow {
  financialCategoryId: number;
  name: string;
  color: string;
  isSystem: boolean;
  budgetedIncome: number;
  budgetedExpense: number;
  actualIncome: number;
  actualExpense: number;
  varianceIncome: number;
  varianceExpense: number;
}

export interface BudgetReport {
  bookYear: {
    bookYearId: number;
    startDate: Date;
    endDate: Date;
    label?: string | null;
  };
  rows: BudgetReportRow[];
  totals: {
    budgetedIncome: number;
    budgetedExpense: number;
    actualIncome: number;
    actualExpense: number;
    varianceIncome: number;
    varianceExpense: number;
  };
}

// P&L detailed
export interface ProfitAndLossCategoryRow {
  financialCategoryId: number;
  name: string;
  color: string;
  isSystem: boolean;
  income: number;
  expense: number;
  budgetedIncome?: number;
  budgetedExpense?: number;
}

export interface ProfitAndLossDetailed {
  bookYear: {
    bookYearId: number;
    startDate: Date;
    endDate: Date;
    label?: string | null;
  };
  categories: ProfitAndLossCategoryRow[];
  totals: {
    totalIncome: number;
    totalExpense: number;
    result: number;
  };
}

export type BalanceSheetWithBookYear = BalanceSheet & {
  bookYear: {
    bookYearId: number;
    startDate: Date;
    endDate: Date;
    label?: string | null;
    asOfDate: Date;
  };
};
